import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { CONSENT_EVENT, CONSENT_KEY, getConsent, Consent } from './consent';
import { claritySafe, initialize, runtime, track, updateConsent, viewPage, EventName, EventMap } from './client';
import { canonicalOrigin, publicPages, publicPath, Service } from './catalog';

export default function AnalyticsRuntime() {
  const location = useLocation();
  const history = useHistory();
  const [consent, setConsent] = useState<Consent>(getConsent);
  useEffect(() => {
    initialize();
    const change = () => { const value = getConsent(); updateConsent(value); setConsent(value); };
    const storage = (event: StorageEvent) => { if (!event.key || event.key === CONSENT_KEY) change(); };
    const resume = () => { if (!document.hidden && getConsent() !== runtime().consent) change(); };
    document.addEventListener('visibilitychange', resume);
    window.addEventListener(CONSENT_EVENT, change);
    window.addEventListener('storage', storage);
    const unguard = history.block(next => {
      if (runtime().loaded && (!publicPath(next.pathname) || !claritySafe(window.location.origin + next.pathname + next.search + next.hash, ''))) {
        // Stop SDKs before a private route or URL containing user input renders.
        window.location.assign(next.pathname + next.search + next.hash);
        return false;
      }
      return undefined;
    });
    return () => { document.removeEventListener('visibilitychange', resume); window.removeEventListener(CONSENT_EVENT, change); window.removeEventListener('storage', storage); unguard(); };
  }, [history]);
  useEffect(() => {
    viewPage(location.pathname);
    const path = publicPath(location.pathname);
    document.title = path ? publicPages[path] : 'Antares Tecnologia GTA';
    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonical) { if (path) canonical.href = canonicalOrigin + path; else canonical.removeAttribute('href'); }
    document.querySelector('meta[name="robots"]')?.setAttribute('content', path ? 'index,follow' : 'noindex,nofollow');
  }, [location.pathname, consent]);
  useEffect(() => {
    if (consent !== 'granted' || !publicPath(location.pathname)) return;
    const onClick = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-analytics-id]') : null;
      if (!target) return;
      const name = target.dataset.analyticsEvent as EventName;
      const params = { cta_id: target.dataset.analyticsId, button_location: target.dataset.analyticsLocation,
        service_name: target.dataset.analyticsService, product_name: target.dataset.analyticsProduct, social_network: target.dataset.analyticsSocial };
      track(name, params as EventMap[typeof name]); // Runtime schema revalidates every DOM attribute.
    };
    const onToggle = (event: Event) => {
      const target = event.target;
      if (target instanceof HTMLDetailsElement && target.open && target.dataset.analyticsFaq) {
        track('faq_open', { faq_id: target.dataset.analyticsFaq as EventMap['faq_open']['faq_id'] });
      }
    };
    let frame = 0;
    const depth = () => Math.min(100, (window.scrollY + window.innerHeight) / Math.max(document.documentElement.scrollHeight, 1) * 100);
    // No retrospective scroll milestones when the visitor accepts midway through a page.
    const baseline = depth();
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        if (document.hidden || document.documentElement.scrollHeight <= window.innerHeight) return;
        for (const percent of [25, 50, 75, 90] as const) if (percent > baseline && depth() >= percent) {
          track('scroll_depth', { percent_scrolled: percent }, `scroll:${percent}`);
        }
      });
    };
    const timers = new Map<Element, ReturnType<typeof setTimeout>>();
    const observed = new Set<Element>();
    const observer = typeof IntersectionObserver !== 'undefined' ? new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const timer = timers.get(entry.target);
        if (timer) { clearTimeout(timer); timers.delete(entry.target); }
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5 && !document.hidden) {
          timers.set(entry.target, setTimeout(() => {
            timers.delete(entry.target);
            if (!entry.target.isConnected || document.hidden) return;
            const service = (entry.target as HTMLElement).dataset.analyticsService as Service;
            track('view_service', { service_name: service, section_id: 'solucoes' }, `service:${service}`);
          }, 1000));
        }
      });
    }, { threshold: [0, 0.5] }) : null;
    const scan = () => {
      for (const target of Array.from(observed)) if (!target.isConnected) {
        observer?.unobserve(target); observed.delete(target);
        const timer = timers.get(target); if (timer) clearTimeout(timer); timers.delete(target);
      }
      document.querySelectorAll('[data-analytics-view="service"]').forEach(target => {
        if (!observed.has(target)) { observed.add(target); observer?.observe(target); }
      });
    };
    const visibility = () => {
      timers.forEach(clearTimeout); timers.clear();
      if (!document.hidden) observed.forEach(target => { observer?.unobserve(target); observer?.observe(target); });
    };
    scan();
    const mutations = new MutationObserver(scan);
    mutations.observe(document.getElementById('root') || document.body, { subtree: true, childList: true });
    document.addEventListener('click', onClick, true);
    document.addEventListener('toggle', onToggle, true);
    document.addEventListener('visibilitychange', visibility);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      document.removeEventListener('click', onClick, true); document.removeEventListener('toggle', onToggle, true);
      document.removeEventListener('visibilitychange', visibility); window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(frame); mutations.disconnect(); observer?.disconnect(); timers.forEach(clearTimeout);
    };
  }, [location.pathname, consent]);
  return null;
}
